import { useEffect, useState, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import DateInput from "../components/DateInput";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Thermometer, Camera, ChevronLeft, ChevronRight,
  AlertTriangle, Snowflake, Wine, Box, Archive, Settings, Minus, Plus, Trash2
} from "lucide-react";
import ElementiCucina from "./anagrafica/ElementiCucina";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const ICONE_FRIGO = {
  "Armadio Refrigerato": Box, "Cella Frigorifera": Archive, "Tavolo Refrigerato": Box,
  "Frigo Vetrina": Box, "Abbattitore": Snowflake, "Cantina Vino": Wine, "Congelatore": Snowflake,
};
const TIPOLOGIE_NEGATIVE = ["Congelatore", "Abbattitore"];

const Frigoriferi = () => {
  const { getAuthHeaders, user, localeCorrente, isAdmin } = useAuth();
  const [frigoriferi, setFrigoriferi] = useState([]);
  const [temperature, setTemperature] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFrigo, setSelectedFrigo] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(() => { const n = new Date(); return n.getFullYear() + "-" + String(n.getMonth() + 1).padStart(2, "0"); });
  const [tempDialogOpen, setTempDialogOpen] = useState(false);
  const [tempForm, setTempForm] = useState({ temperatura: "", isNegative: false, data: "", ora: "", nota_anomalia: "" });
  const [ocrLoading, setOcrLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [alerts, setAlerts] = useState([]);
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [rispostaAdmin, setRispostaAdmin] = useState("");
  const [verificaTemp, setVerificaTemp] = useState("");
  const [verificaNegative, setVerificaNegative] = useState(false);
  const [frigoDialogOpen, setFrigoDialogOpen] = useState(false);
  const [dayDetailOpen, setDayDetailOpen] = useState(false);
  const [selectedDayDate, setSelectedDayDate] = useState(null);
  const [selectedDayTemps, setSelectedDayTemps] = useState([]);

  const fetchData = async () => {
    if (!localeCorrente) return;
    try {
      const headers = getAuthHeaders();
      const lp = `locale_id=${localeCorrente.id}`;
      const [frigoRes, alertsRes] = await Promise.all([
        axios.get(`${API}/frigoriferi?${lp}`, { headers }),
        axios.get(`${API}/temperature-alerts?${lp}`, { headers }).catch(() => ({ data: [] })),
      ]);
      setFrigoriferi(frigoRes.data);
      setAlerts(alertsRes.data.filter(a => a.stato !== "risolto" && a.stato !== "archiviato"));
    } catch { toast.error("Errore nel caricamento"); }
    finally { setLoading(false); }
  };

  const fetchTemperature = async (frigoId, month) => {
    try {
      const headers = getAuthHeaders();
      const [year, m] = month.split("-").map(Number);
      const dataInizio = `${year}-${String(m).padStart(2, "0")}-01`;
      const lastDay = new Date(year, m, 0).getDate();
      const dataFine = `${year}-${String(m).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
      const res = await axios.get(`${API}/temperature?frigorifero_id=${frigoId}&data_inizio=${dataInizio}&data_fine=${dataFine}`, { headers });
      setTemperature(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchData(); }, [localeCorrente]);
  useEffect(() => { if (selectedFrigo) fetchTemperature(selectedFrigo.id, currentMonth); }, [selectedFrigo, currentMonth]);

  // Helpers
  const formatDateIT = (s) => { if (!s) return "-"; const d = new Date(s); return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" }); };
  const formatDateTime = (s) => { if (!s) return ""; try { return new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return s; } };
  const getMonthName = (ms) => { const [y, m] = ms.split("-"); return new Date(y, parseInt(m) - 1, 1).toLocaleDateString("it-IT", { month: "long", year: "numeric" }); };
  const isTempAnomala = (temp, frigo) => {
    if (!frigo || temp === "" || temp === null) return false;
    const t = parseFloat(temp); if (isNaN(t)) return false;
    const tMin = Math.min(frigo.temperatura_min ?? 0, frigo.temperatura_max ?? 4);
    const tMax = Math.max(frigo.temperatura_min ?? 0, frigo.temperatura_max ?? 4);
    return t < tMin || t > tMax;
  };
  const getRealTemp = () => { const v = parseFloat(tempForm.temperatura); if (isNaN(v)) return NaN; return tempForm.isNegative ? -Math.abs(v) : Math.abs(v); };
  const frigoAlerts = (frigoId) => alerts.filter(a => a.frigorifero_id === frigoId);
  const parseTime = (ora) => { const [h, m] = (ora || "00:00").split(":").map(Number); return (h || 0) * 60 + (m || 0); };

  const generateCalendar = () => {
    const [year, month] = currentMonth.split("-").map(Number);
    const daysInMonth = new Date(year, month, 0).getDate();
    const startDow = (new Date(year, month - 1, 1).getDay() + 6) % 7;
    const days = [];

    // Raggruppa le temperature per giorno: prendi l'ultima registrata
    const tempByDay = {};
    const countByDay = {};
    for (const t of temperature) {
      const d = t.data;
      if (!countByDay[d]) countByDay[d] = 0;
      countByDay[d]++;
      // Mantieni sempre l'ultima registrazione (per ora)
      if (!tempByDay[d] || parseTime(t.ora) >= parseTime(tempByDay[d].ora)) {
        tempByDay[d] = t;
      }
    }

    for (let i = 0; i < startDow; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      days.push({ day: d, date: dateStr, temperature: tempByDay[dateStr] || null, count: countByDay[dateStr] || 0 });
    }
    return days;
  };

  const openTempDialog = (dateStr = null) => {
    const isNeg = selectedFrigo && TIPOLOGIE_NEGATIVE.includes(selectedFrigo.tipologia);
    setTempForm({ temperatura: "", isNegative: isNeg, data: dateStr || new Date().toISOString().split("T")[0], ora: new Date().toTimeString().slice(0, 5), nota_anomalia: "" });
    setTempDialogOpen(true);
  };

  const openDayDetail = (dateStr) => {
    const dayTemps = temperature.filter(t => t.data === dateStr).sort((a, b) => parseTime(a.ora) - parseTime(b.ora));
    setSelectedDayDate(dateStr);
    setSelectedDayTemps(dayTemps);
    setDayDetailOpen(true);
  };

  const handleDeleteTemperatura = async (tempId) => {
    try {
      await axios.delete(`${API}/temperature/${tempId}`, { headers: getAuthHeaders() });
      toast.success("Temperatura eliminata");
      setSelectedDayTemps(prev => prev.filter(t => t.id !== tempId));
      fetchTemperature(selectedFrigo.id, currentMonth);
    } catch { toast.error("Errore eliminazione"); }
  };

  const handleTempSubmit = async (e) => {
    e.preventDefault();
    if (!tempForm.temperatura) { toast.error("Inserisci la temperatura"); return; }
    const realTemp = getRealTemp();
    if (isNaN(realTemp)) { toast.error("Temperatura non valida"); return; }
    if (tempForm.data > new Date().toISOString().split("T")[0]) { toast.error("Non puoi registrare temperature per date future"); return; }
    const isAnomala = isTempAnomala(realTemp, selectedFrigo);
    if (isAnomala && !tempForm.nota_anomalia.trim()) { toast.error("Temperatura fuori range: descrivi il possibile problema"); return; }
    try {
      const res = await axios.post(`${API}/temperature`, {
        locale_id: localeCorrente.id, frigorifero_id: selectedFrigo.id, temperatura: realTemp,
        data: tempForm.data, ora: tempForm.ora, operatore_id: user?.id,
        operatore_nome: user?.nome || user?.email || user?.telefono, nota_anomalia: isAnomala ? tempForm.nota_anomalia : null,
      }, { headers: getAuthHeaders() });
      if (res.data.alert_id) toast.warning("Temperatura anomala! Alert creato.", { duration: 5000 });
      else if (res.data.alerts_resolved) toast.success(`Temperatura OK! ${res.data.alerts_resolved} alert risolti.`);
      else toast.success("Temperatura registrata");
      setTempDialogOpen(false);
      fetchTemperature(selectedFrigo.id, currentMonth);
      fetchData();
    } catch { toast.error("Errore nel salvataggio"); }
  };

  const handlePhotoCapture = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setOcrLoading(true);
    try {
      const base64 = await new Promise(r => { const rd = new FileReader(); rd.onload = () => r(rd.result.split(",")[1]); rd.readAsDataURL(file); });
      const res = await axios.post(`${API}/ocr/temperatura`, { image_base64: base64 }, { headers: getAuthHeaders() });
      if (res.data.success && res.data.temperatura !== null) {
        const t = res.data.temperatura;
        setTempForm(prev => ({ ...prev, temperatura: Math.abs(t).toString(), isNegative: t < 0 || prev.isNegative }));
        toast.success(`Temperatura rilevata: ${t}°C`);
      } else toast.warning("Temperatura non rilevata - inserisci manualmente");
    } catch { toast.error("Errore OCR"); }
    finally { setOcrLoading(false); e.target.value = ""; }
  };

  const handleArchiveAlert = async (alertId) => {
    try { await axios.put(`${API}/temperature-alerts/${alertId}/archivia`, {}, { headers: getAuthHeaders() }); toast.success("Alert archiviato"); fetchData(); }
    catch { toast.error("Errore archiviazione"); }
  };

  const handleExportReport = async () => {
    try {
      const res = await axios.get(`${API}/report/temperature/${selectedFrigo.id}`, { headers: getAuthHeaders(), params: { mese: currentMonth } });
      const report = res.data;
      const limMin = report.frigorifero.temperatura_min_norm;
      const limMax = report.frigorifero.temperatura_max_norm;

      const righe = report.registrazioni.map(r => {
        const esito = r.is_anomala ? "FUORI RANGE" : "OK";
        const cls = r.is_anomala ? "anomala" : "ok";
        const nota = r.nota_anomalia || "";
        let statoAlert = "-";
        if (r.alert_stato === "risolto") statoAlert = '<span class="stato-risolto">RISOLTO</span>';
        else if (r.alert_stato === "archiviato") statoAlert = '<span class="stato-archiviato">ARCHIVIATO</span>';
        else if (r.alert_stato === "aperto") statoAlert = '<span class="stato-aperto">APERTO</span>';
        else if (r.is_anomala) statoAlert = '<span class="stato-aperto">NON SEGNALATO</span>';
        const operatore = r.operatore_nome || r.operatore || "-";
        return '<tr class="' + cls + '"><td>' + formatDateIT(r.data) + '</td><td>' + r.ora + '</td><td><strong>' + r.temperatura + '\u00B0C</strong></td><td>' + operatore + '</td><td>' + esito + '</td><td>' + nota + '</td><td>' + statoAlert + '</td></tr>';
      }).join("");

      const html = '<!DOCTYPE html><html><head><title>Registro Temperature - ' + report.frigorifero.nome + '</title>' +
        '<style>' +
        'body{font-family:Arial,sans-serif;padding:24px;color:#1a1a1a}' +
        'h1{font-size:20px;margin-bottom:4px}' +
        'h2{font-size:14px;color:#555;margin-bottom:2px}' +
        '.limiti{font-size:15px;font-weight:bold;margin:8px 0 16px}' +
        'table{width:100%;border-collapse:collapse;margin-top:12px}' +
        'th,td{border:1px solid #333;padding:8px 10px;text-align:center;font-size:12px}' +
        'th{background:#e8e8e8;font-weight:bold}' +
        '.anomala{background:#fee2e2}' +
        '.ok{background:#dcfce7}' +
        '.stato-risolto{color:#16a34a;font-weight:bold}' +
        '.stato-archiviato{color:#2563eb;font-weight:bold}' +
        '.stato-aperto{color:#dc2626;font-weight:bold}' +
        '.stats{margin-top:20px;font-size:13px;line-height:1.8}' +
        '.firma{margin-top:40px;font-size:11px}' +
        '</style></head><body>' +
        '<h1>REGISTRO TEMPERATURE HACCP</h1>' +
        '<h2>' + report.frigorifero.nome + ' - ' + getMonthName(report.mese) + '</h2>' +
        '<p class="limiti">Range consentito: ' + limMin + '\u00B0C / ' + limMax + '\u00B0C</p>' +
        '<table>' +
        '<tr><th>Data</th><th>Ora</th><th>Temp</th><th>Operatore</th><th>Esito</th><th>Note / Azione correttiva</th><th>Stato segnalazione</th></tr>' +
        righe +
        '</table>' +
        '<div class="stats">' +
        '<strong>Riepilogo:</strong> ' + report.statistiche.totale_giorni + ' registrazioni | Media: ' + report.statistiche.temperatura_media + '\u00B0C | Anomalie: ' + report.statistiche.anomalie + ' | Risolte: ' + report.statistiche.anomalie_risolte + '<br/>' +
        'Generato: ' + new Date().toLocaleString("it-IT") +
        '</div>' +
        '<p class="firma">Firma responsabile: _______________________</p>' +
        '</body></html>';

      const pw = window.open("", "_blank");
      pw.document.write(html);
      pw.document.close(); pw.print();
    } catch { toast.error("Errore generazione report"); }
  };

  // ─── Sign Toggle Component ──────────────────────────────────
  const SignToggle = ({ isNegative, onChange, size = "normal" }) => (
    <button type="button" onClick={() => onChange(!isNegative)}
      className={`${size === "small" ? "h-10 w-10" : "h-14 w-14"} rounded-2xl flex items-center justify-center font-bold transition-all border-2 shrink-0 ${
        isNegative ? "bg-blue-500/20 border-blue-500 text-blue-400" : "bg-red-500/10 border-border text-foreground/50"
      }`} data-testid="temp-sign-toggle"
      title={isNegative ? "Negativo (sotto zero)" : "Positivo (sopra zero)"}>
      {isNegative ? <Minus className={size === "small" ? "h-5 w-5" : "h-6 w-6"} /> : <Plus className={size === "small" ? "h-5 w-5" : "h-6 w-6"} />}
    </button>
  );

  // ═══ VISTA DETTAGLIO FRIGO ═════════════════════════════════
  if (selectedFrigo) {
    const calendar = generateCalendar();
    const Icon = ICONE_FRIGO[selectedFrigo.tipologia] || Box;
    const myAlerts = frigoAlerts(selectedFrigo.id);

    return (
      <div className="space-y-4" data-testid="frigo-detail">
        {/* Header */}
        <div className="flex items-center gap-3">
          <button onClick={() => setSelectedFrigo(null)} className="p-2 rounded-full bg-muted"><ChevronLeft className="h-5 w-5 text-foreground" /></button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-foreground truncate">{selectedFrigo.nome}</h1>
            <p className="text-xs text-muted-foreground">{selectedFrigo.tipologia} &bull; {Math.min(selectedFrigo.temperatura_min ?? 0, selectedFrigo.temperatura_max ?? 4)}°C / {Math.max(selectedFrigo.temperatura_min ?? 0, selectedFrigo.temperatura_max ?? 4)}°C</p>
          </div>
          <button onClick={handleExportReport} className="px-3 py-2 bg-muted text-foreground rounded-xl text-xs font-medium">Report</button>
        </div>

        {/* Alert attivi per questo frigo */}
        {myAlerts.map(alert => (
          <div key={alert.id} className={`p-3.5 rounded-2xl border ${alert.stato === "da_tecnico" ? "bg-red-500/15 border-red-500/40" : alert.stato === "in_attesa" ? "bg-amber-500/15 border-amber-500/40" : "bg-red-500/10 border-red-500/30"}`} data-testid={`alert-card-${alert.id}`}>
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-red-400" />
                <span className="text-sm font-bold text-foreground">{alert.temperatura}°C</span>
                <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full ${alert.stato === "da_tecnico" ? "bg-red-500 text-white" : alert.stato === "in_attesa" ? "bg-amber-500 text-black" : "bg-red-500/60 text-white"}`}>
                  {alert.stato === "da_tecnico" ? "TECNICO" : alert.stato === "in_attesa" ? "In attesa" : "Aperto"}
                </span>
              </div>
              <button onClick={() => handleArchiveAlert(alert.id)} className="p-1 rounded-lg hover:bg-muted" data-testid={`archive-alert-${alert.id}`}>
                <Archive className="h-4 w-4 text-muted-foreground" />
              </button>
            </div>
            <p className="text-xs text-muted-foreground">{formatDateTime(alert.created_at)} &bull; Range: {alert.range_min}°C / {alert.range_max}°C</p>
            {alert.nota_anomalia && <p className="text-sm text-foreground/80 mt-1"><strong>{alert.operatore_nome}:</strong> {alert.nota_anomalia}</p>}
            {alert.risposta_admin && <div className="mt-2 p-2 bg-primary/10 rounded-xl text-sm text-primary"><strong>{alert.admin_nome || "Admin"}:</strong> {alert.risposta_admin}</div>}
            
            {alert.stato === "in_attesa" && (
              <button onClick={() => { setSelectedAlert(alert); setVerificaTemp(""); setVerificaNegative(false); setAlertDialogOpen(true); }}
                className="mt-2 w-full py-2 bg-amber-500 text-black rounded-xl font-bold text-sm" data-testid="btn-verifica-alert">
                Registra Temperatura di Verifica
              </button>
            )}
            {alert.stato === "aperto" && isAdmin && (
              <button onClick={() => { setSelectedAlert(alert); setRispostaAdmin(""); setAlertDialogOpen(true); }}
                className="mt-2 w-full py-2 bg-primary text-white rounded-xl font-bold text-sm" data-testid="btn-rispondi-alert">
                Rispondi
              </button>
            )}
          </div>
        ))}

        {/* Month navigator */}
        <div className="flex items-center justify-between">
          <button onClick={() => { const [y, m] = currentMonth.split("-").map(Number); const d = new Date(y, m - 2, 1); setCurrentMonth(d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0")); }} className="p-2 rounded-full bg-muted" data-testid="month-prev-btn"><ChevronLeft className="h-4 w-4 text-foreground" /></button>
          <h2 className="text-base font-bold text-foreground capitalize">{getMonthName(currentMonth)}</h2>
          <button onClick={() => { const [y, m] = currentMonth.split("-").map(Number); const d = new Date(y, m, 1); setCurrentMonth(d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0")); }} className="p-2 rounded-full bg-muted" data-testid="month-next-btn"><ChevronRight className="h-4 w-4 text-foreground" /></button>
        </div>

        {/* Calendar */}
        <div className="bg-card rounded-2xl p-3 border border-border">
          <div className="grid grid-cols-7 gap-1.5 mb-2">
            {["L", "M", "M", "G", "V", "S", "D"].map((d, i) => <div key={i} className="text-center text-xs text-muted-foreground font-semibold py-1">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1.5">
            {calendar.map((item, idx) => {
              if (!item) return <div key={`e-${idx}`} className="aspect-square" />;
              const hasTemp = item.temperature;
              const isAnomala = hasTemp && isTempAnomala(item.temperature.temperatura, selectedFrigo);
              const isToday = item.date === new Date().toISOString().split("T")[0];
              const isFuturo = item.date > new Date().toISOString().split("T")[0];
              return (
                <button key={item.date} onClick={() => { if (!isFuturo) openDayDetail(item.date); }}
                  disabled={isFuturo}
                  className={`aspect-square rounded-xl flex flex-col items-center justify-center gap-0.5 transition-all relative
                    ${isToday ? "ring-2 ring-emerald-500 ring-offset-1" : ""}
                    ${isFuturo ? "opacity-30 cursor-not-allowed" : ""}
                    ${hasTemp ? isAnomala ? "bg-red-100 border-2 border-red-400" : "bg-emerald-100 border-2 border-emerald-400" : "bg-card border border-border hover:bg-muted"}`}>
                  <span className={`text-lg font-bold ${hasTemp ? "text-foreground" : "text-muted-foreground"}`}>{item.day}</span>
                  {hasTemp && <span className={`text-2xl font-black leading-none ${isAnomala ? "text-red-700" : "text-emerald-700"}`}>{item.temperature.temperatura}°</span>}
                  {item.count > 1 && <span className="absolute top-0.5 right-1 text-[9px] font-bold text-foreground bg-card/80 rounded-full px-1">{item.count}x</span>}
                </button>
              );
            })}
          </div>
        </div>

        <button onClick={() => openTempDialog()} className="w-full flex items-center justify-center gap-3 p-3.5 bg-lime-400 text-black rounded-2xl font-bold active:scale-[0.98]" data-testid="btn-registra-temp">
          <Thermometer className="h-5 w-5" /> Registra Temperatura
        </button>

        <div className="flex gap-4 text-xs">
          <div className="flex items-center gap-1.5"><div className="w-3.5 h-3.5 rounded bg-emerald-100 border-2 border-emerald-400" /><span className="text-foreground font-medium">In range</span></div>
          <div className="flex items-center gap-1.5"><div className="w-3.5 h-3.5 rounded bg-red-100 border-2 border-red-400" /><span className="text-foreground font-medium">Fuori range</span></div>
          <div className="flex items-center gap-1.5"><div className="w-3.5 h-3.5 rounded bg-card border border-border" /><span className="text-foreground font-medium">Vuoto</span></div>
        </div>

        {/* Dialog Alert verifica/risposta */}
        <Dialog open={alertDialogOpen} onOpenChange={setAlertDialogOpen}>
          <DialogContent className="max-w-sm rounded-3xl bg-card border-border">
            <DialogHeader>
              <DialogTitle className="text-foreground flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-400" />
                {selectedAlert?.stato === "aperto" ? "Rispondi all'Alert" : "Verifica Temperatura"}
              </DialogTitle>
            </DialogHeader>
            {selectedAlert?.stato === "aperto" ? (
              <div className="space-y-4">
                <div className="p-3 bg-red-500/10 rounded-xl text-sm">
                  <p><strong>{selectedAlert?.operatore_nome}:</strong> {selectedAlert?.nota_anomalia}</p>
                  <p className="text-muted-foreground mt-1">Temp: {selectedAlert?.temperatura}°C &bull; {formatDateTime(selectedAlert?.created_at)}</p>
                </div>
                <textarea value={rispostaAdmin} onChange={e => setRispostaAdmin(e.target.value)} placeholder="Istruzioni per risolvere..." rows={3} className="w-full p-3 bg-card border border-border text-foreground rounded-xl resize-none" />
                <DialogFooter className="gap-2">
                  <Button variant="outline" onClick={() => setAlertDialogOpen(false)} className="flex-1 border-border">Annulla</Button>
                  <Button onClick={async () => {
                    if (!rispostaAdmin.trim()) { toast.error("Scrivi le istruzioni"); return; }
                    try { await axios.put(`${API}/temperature-alerts/${selectedAlert.id}/risposta`, { risposta: rispostaAdmin, admin_id: user?.id, admin_nome: user?.nome || user?.email || user?.telefono }, { headers: getAuthHeaders() }); toast.success("Istruzioni inviate"); setAlertDialogOpen(false); fetchData(); }
                    catch { toast.error("Errore"); }
                  }} className="flex-1 bg-primary text-white font-bold">Invia</Button>
                </DialogFooter>
              </div>
            ) : (
              <div className="space-y-4">
                {selectedAlert?.risposta_admin && <div className="p-3 bg-primary/10 rounded-xl text-sm text-primary"><strong>Istruzioni:</strong> {selectedAlert.risposta_admin}</div>}
                <div className="space-y-2">
                  <Label className="text-foreground/70">Nuova temperatura (°C)</Label>
                  <div className="flex gap-2">
                    <SignToggle isNegative={verificaNegative} onChange={setVerificaNegative} size="small" />
                    <Input type="number" step="0.1" min="0" value={verificaTemp} onChange={e => setVerificaTemp(e.target.value)}
                      placeholder="Temperatura attuale" className="h-14 bg-card border-border text-foreground text-xl text-center font-bold rounded-2xl flex-1" />
                  </div>
                </div>
                <DialogFooter className="gap-2">
                  <Button variant="outline" onClick={() => setAlertDialogOpen(false)} className="flex-1 border-border">Annulla</Button>
                  <Button onClick={async () => {
                    if (!verificaTemp) { toast.error("Inserisci la temperatura"); return; }
                    const rv = verificaNegative ? -Math.abs(parseFloat(verificaTemp)) : Math.abs(parseFloat(verificaTemp));
                    try {
                      const res = await axios.put(`${API}/temperature-alerts/${selectedAlert.id}/verifica`, {
                        locale_id: localeCorrente.id, frigorifero_id: selectedAlert.frigorifero_id,
                        temperatura: rv, operatore_id: user?.id, operatore_nome: user?.nome || user?.email || user?.telefono
                      }, { headers: getAuthHeaders() });
                      if (res.data.stato === "risolto") toast.success("Temperatura OK! Alert chiuso.");
                      else toast.error("Ancora fuori range.", { duration: 6000 });
                      setAlertDialogOpen(false); fetchData();
                      if (selectedFrigo) fetchTemperature(selectedFrigo.id, currentMonth);
                    } catch { toast.error("Errore"); }
                  }} className="flex-1 bg-amber-500 text-black font-bold">Conferma</Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Dialog dettaglio temperature del giorno */}
        <Dialog open={dayDetailOpen} onOpenChange={setDayDetailOpen}>
          <DialogContent className="max-w-sm rounded-3xl bg-card border-border">
            <DialogHeader>
              <DialogTitle className="text-foreground flex items-center gap-2">
                <Thermometer className="h-5 w-5 text-primary" />
                {selectedDayDate ? new Date(selectedDayDate + "T12:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" }) : ""}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {selectedDayTemps.length === 0 ? (
                <p className="text-muted-foreground text-sm text-center py-4">Nessuna temperatura registrata</p>
              ) : (
                selectedDayTemps.map(t => {
                  const isAnomala = isTempAnomala(t.temperatura, selectedFrigo);
                  return (
                    <div key={t.id} data-testid={`day-temp-row-${t.id}`}
                      className={`flex items-center justify-between p-3 rounded-xl border ${isAnomala ? "bg-red-500/10 border-red-400/40" : "bg-emerald-500/10 border-emerald-400/40"}`}>
                      <div className="flex-1 min-w-0">
                        <span className={`text-lg font-bold ${isAnomala ? "text-red-500" : "text-emerald-600"}`}>{t.temperatura}°C</span>
                        <p className="text-xs text-muted-foreground">{t.ora} &bull; {t.operatore_nome || "Operatore"}</p>
                        {t.nota_anomalia && <p className="text-xs text-red-400 mt-0.5 truncate">{t.nota_anomalia}</p>}
                      </div>
                      {isAdmin && (
                        <button onClick={() => handleDeleteTemperatura(t.id)}
                          className="p-2 ml-2 rounded-lg hover:bg-red-500/20 text-red-400 transition-colors shrink-0"
                          data-testid={`delete-temp-${t.id}`} title="Elimina temperatura">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
            <DialogFooter>
              <Button onClick={() => { setDayDetailOpen(false); openTempDialog(selectedDayDate); }}
                className="w-full bg-primary text-white font-bold gap-2"
                data-testid="btn-aggiungi-temp-giorno">
                <Plus className="h-4 w-4" /> Aggiungi temperatura
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog registrazione temperatura */}
        <Dialog open={tempDialogOpen} onOpenChange={setTempDialogOpen}>
          <DialogContent className="max-w-sm rounded-3xl bg-card border-border">
            <DialogHeader>
              <DialogTitle className="text-foreground flex items-center gap-2">
                <Thermometer className="h-5 w-5 text-primary" /> Registra Temperatura
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleTempSubmit} className="space-y-4">
              <input ref={fileInputRef} type="file" accept="image/*" capture="environment" onChange={handlePhotoCapture} className="hidden" />
              <button type="button" onClick={() => fileInputRef.current?.click()} disabled={ocrLoading}
                className="w-full flex items-center justify-center gap-3 p-3 bg-muted text-foreground rounded-2xl font-medium disabled:opacity-50">
                {ocrLoading ? <span className="animate-pulse">Analisi...</span> : <><Camera className="h-5 w-5" /> Scatta foto al display</>}
              </button>
              <div className="text-center text-muted-foreground text-xs">oppure inserisci manualmente</div>

              <div className="space-y-2">
                <Label className="text-foreground/70">Temperatura (°C)</Label>
                <div className="flex gap-2 items-center">
                  <SignToggle isNegative={tempForm.isNegative} onChange={v => setTempForm(p => ({ ...p, isNegative: v }))} />
                  <Input type="number" step="0.1" min="0" value={tempForm.temperatura}
                    onChange={e => setTempForm(p => ({ ...p, temperatura: e.target.value }))}
                    placeholder="es. 4.0" data-testid="temp-input"
                    className={`h-14 bg-card border-border text-foreground text-xl text-center font-bold rounded-2xl flex-1 ${
                      tempForm.temperatura && isTempAnomala(getRealTemp(), selectedFrigo)
                        ? "border-red-500 bg-red-500/10 text-red-400" : tempForm.temperatura ? "border-lime-500 bg-lime-500/10" : ""}`} />
                </div>
                {tempForm.temperatura && (
                  <p className="text-center text-sm font-semibold" style={{ color: isTempAnomala(getRealTemp(), selectedFrigo) ? "#ef4444" : "#84cc16" }}>
                    {tempForm.isNegative ? "-" : ""}{tempForm.temperatura}°C
                  </p>
                )}
                <p className="text-[10px] text-muted-foreground text-center">
                  Range: {Math.min(selectedFrigo?.temperatura_min ?? 0, selectedFrigo?.temperatura_max ?? 4)}°C / {Math.max(selectedFrigo?.temperatura_min ?? 0, selectedFrigo?.temperatura_max ?? 4)}°C
                </p>
              </div>

              {tempForm.temperatura && isTempAnomala(getRealTemp(), selectedFrigo) && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-2xl space-y-2">
                  <Label className="text-red-400 font-medium flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Fuori range! Descrivi il problema *</Label>
                  <textarea value={tempForm.nota_anomalia} onChange={e => setTempForm(p => ({ ...p, nota_anomalia: e.target.value }))}
                    placeholder="Es. guarnizione porta, frigo aperto..." rows={2} className="w-full p-2 bg-card border border-border text-foreground rounded-xl resize-none text-sm" />
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-foreground/70 text-xs">Data</Label>
                  <DateInput value={tempForm.data} onChange={(v) => setTempForm(p => ({ ...p, data: v }))} />
                </div>
                <div className="space-y-1">
                  <Label className="text-foreground/70 text-xs">Ora</Label>
                  <Input type="time" value={tempForm.ora} onChange={e => setTempForm(p => ({ ...p, ora: e.target.value }))} className="h-10 bg-card border-border text-foreground rounded-xl text-sm" />
                </div>
              </div>

              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setTempDialogOpen(false)} className="flex-1 border-border">Annulla</Button>
                <Button type="submit" className="flex-1 bg-primary text-white font-bold">Salva</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ═══ LISTA FRIGORIFERI (COMPATTA) ══════════════════════════
  return (
    <div className="page-container space-y-4" data-testid="frigoriferi-page">
      <div className="hidden md:block">
        <h1 className="page-title">Controllo Temperature</h1>
        <p className="page-subtitle">Registra e monitora le temperature HACCP</p>
      </div>

      {isAdmin && (
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground text-sm">{frigoriferi.length} frigoriferi configurati</p>
          <Button variant="outline" size="sm" onClick={() => setFrigoDialogOpen(true)} className="gap-1.5 rounded-xl" data-testid="open-postazioni-btn">
            <Settings className="h-4 w-4" /> I miei frigo
          </Button>
        </div>
      )}

      {loading ? (
        <div className="loading-state"><div className="text-muted-foreground">Caricamento...</div></div>
      ) : frigoriferi.length === 0 ? (
        <div className="empty-state">
          <Thermometer className="empty-state-icon" />
          <p className="empty-state-text">Nessun frigorifero registrato</p>
          <p className="text-xs text-muted-foreground mt-2">Clicca "I miei frigo" per aggiungerne</p>
        </div>
      ) : (
        <div className="space-y-2">
          {frigoriferi.map(frigo => {
            const Icon = ICONE_FRIGO[frigo.tipologia] || Box;
            const hasAlert = frigoAlerts(frigo.id).length > 0;
            const alertCount = frigoAlerts(frigo.id).length;
            return (
              <button key={frigo.id} onClick={() => setSelectedFrigo(frigo)}
                className={`w-full flex items-center gap-3 p-3.5 rounded-2xl border transition-all active:scale-[0.98] bg-card hover:bg-muted text-left ${hasAlert ? "border-red-500/50" : "border-border"}`}
                data-testid={`frigo-card-${frigo.id}`}>
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${hasAlert ? "bg-red-500/20" : "bg-lime-400/20"}`}>
                  <Icon className={`h-5 w-5 ${hasAlert ? "text-red-400" : "text-lime-400"}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-foreground truncate">{frigo.nome}</p>
                  <p className="text-xs text-muted-foreground">{frigo.tipologia} &bull; {Math.min(frigo.temperatura_min ?? 0, frigo.temperatura_max ?? 4)}°C / {Math.max(frigo.temperatura_min ?? 0, frigo.temperatura_max ?? 4)}°C</p>
                </div>
                {hasAlert && (
                  <div className="flex items-center gap-1 shrink-0 px-2 py-1 bg-red-500/15 rounded-lg" data-testid={`frigo-alert-badge-${frigo.id}`}>
                    <AlertTriangle className="h-3.5 w-3.5 text-red-400" />
                    <span className="text-xs font-bold text-red-400">{alertCount}</span>
                  </div>
                )}
                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
              </button>
            );
          })}
        </div>
      )}

      <Dialog 
        open={frigoDialogOpen} 
        onOpenChange={(open) => { 
          setFrigoDialogOpen(open); 
          if (!open) fetchData(); // refresh lista quando il dialog chiude
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <ElementiCucina embedded frigoOnly headerTitle="I miei frigo" />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Frigoriferi;
